import { cn } from '@/lib/utils';
import type { MetricTierColor } from '@skystone/shared';
import { useMetricDefinitions } from '@/hooks/use-metric-definition';

// ---------------------------------------------------------------------------
// Static colour mapping (used when tier data from the provider is available)
// ---------------------------------------------------------------------------

const COLOR_CLASS: Record<MetricTierColor, string> = {
  success: 'text-success',
  primary: 'text-primary',
  warning: 'text-warning',
  error: 'text-error',
};

// Fallback: map well-known tier ids to colours when provider data is unavailable
const FALLBACK_COLOR: Record<string, string> = {
  elite: 'text-success',
  high: 'text-primary',
  medium: 'text-warning',
  low: 'text-error',
  on_target: 'text-success',
  excellent: 'text-success',
  good: 'text-primary',
  watch: 'text-warning',
  at_risk: 'text-error',
  slow: 'text-error',
  healthy: 'text-success',
  too_high: 'text-error',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MetricLevelBadgeProps {
  level: string | null | undefined;
  className?: string;
}

function resolveColorClass(level: string, definitions: { tiers: { tierId: string; colorToken: MetricTierColor }[] }[]): string {
  for (const def of definitions) {
    const tier = def.tiers.find((t) => t.tierId === level);
    if (tier) return COLOR_CLASS[tier.colorToken];
  }
  return FALLBACK_COLOR[level] ?? 'text-muted-foreground';
}

export function MetricLevelBadge({ level, className }: Readonly<MetricLevelBadgeProps>) {
  const { definitions } = useMetricDefinitions();

  if (!level) return <span className="text-xs text-muted-foreground">—</span>;

  const colorClass = resolveColorClass(level, definitions);
  const label = level.replaceAll('_', ' ');

  return (
    <span className={cn('text-[10px] font-semibold uppercase', colorClass, className)}>
      {label}
    </span>
  );
}
